<script setup>
import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue'
import { DriverStore } from '../pinia/Driver'

const PRIZE_H = 112
const GAP = 24
const STRIDE = PRIZE_H + GAP
const WIN_INDEX = 72
const REEL_LEN = 90
const FADE_MS = 280
const CASH_MIN = 150000
const CASH_MAX_PREMIUM = 2000000
const CASH_MAX_NORMAL = 1000000
const CASH_STEP = 25000
const SB_MIN = 1500
const SB_MAX_PREMIUM = 15000
const SB_MAX_NORMAL = 10000
const SB_STEP = 500
const TYPE_PCT = 100 / 3
const CAR_CDN = 'https://cdn.nightriderz.world/images/website/moonset/cars'

const props = defineProps({
  modelValue: Boolean,
  mode: {
    type: String,
    default: 'premium',
    validator: (value) => value === 'premium' || value === 'normal',
  },
  wheelspins: { type: Number, default: 0 },
})
const emit = defineEmits(['update:modelValue', 'claimed'])
const { proxy } = getCurrentInstance()
const Driver = DriverStore()

const state = ref('idle')
const error = ref('')
const rewards = ref([])
const claiming = ref(false)
const landed = ref([false, false, false])
const mix = ref([])
const pool = ref({ cash: [], sb: [], cars: [] })
const poolReady = ref(false)
const ratesOpen = ref(false)
const soft = ref(false)
const reelsEl = ref(null)
const reels = ref([
  { items: [], offset: 0, transition: 'none', landed: false },
  { items: [], offset: 0, transition: 'none', landed: false },
  { items: [], offset: 0, transition: 'none', landed: false },
])

const normal = computed(() => props.mode === 'normal')
const cashMax = computed(() => normal.value ? CASH_MAX_NORMAL : CASH_MAX_PREMIUM)
const sbMax = computed(() => normal.value ? SB_MAX_NORMAL : SB_MAX_PREMIUM)
const cashAmounts = computed(() => {
  const values = []
  for (let value = CASH_MIN; value <= cashMax.value; value += CASH_STEP) values.push(value)
  return values
})
const boostAmounts = computed(() => {
  const values = []
  for (let value = SB_MIN; value <= sbMax.value; value += SB_STEP) values.push(value)
  return values
})
const cashChance = computed(() => TYPE_PCT / Math.max(1, cashAmounts.value.length))
const boostChance = computed(() => TYPE_PCT / Math.max(1, boostAmounts.value.length))
const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})
const kicker = computed(() => normal.value ? 'Wheelspin' : 'Premium')
const heading = computed(() => {
  if (state.value === 'done') return normal.value ? 'Spin complete' : 'Drop complete'
  if (state.value === 'spinning') return 'Spinning…'
  if (state.value === 'error') return 'Unable to claim'
  return normal.value ? 'Ready to spin' : 'Weekly drop'
})
const subtitle = computed(() => {
  if (state.value === 'done') return 'Rewards were added to your active driver.'
  if (state.value === 'error') return error.value
  if (normal.value && state.value === 'idle') {
    const count = Math.max(0, Number(props.wheelspins) || 0)
    return count === 1 ? '1 spin remaining' : `${count} spins remaining`
  }
  return ''
})
const actionLabel = computed(() => {
  if (state.value === 'done') return normal.value && props.wheelspins > 0 ? 'Spin again' : 'Done'
  if (state.value === 'error') return 'Try again'
  return 'Spin now'
})
const actionIcon = computed(() => (
  state.value === 'done' && !(normal.value && props.wheelspins > 0) ? 'mdi-check' : 'mdi-rotate-right'
))

function formatAmount(value) {
  return Number(value || 0).toLocaleString('en-US')
}
function brand(item) {
  return String(item?.manufactor || item?.manufacturer || '').trim()
}
function modelName(item) {
  const model = String(item?.model || '').trim()
  if (model) return model
  const full = String(item?.full_name || item?.label || item?.store_name || '').trim()
  const maker = brand(item)
  if (maker && full.toLowerCase().startsWith(maker.toLowerCase())) {
    return full.slice(maker.length).trim() || full
  }
  return full || 'Car'
}
function moneyLabel(item) {
  if (!item) return ''
  if (item.type === 'cash') return `${formatAmount(item.amount)} $`
  if (item.type === 'sb') return `${formatAmount(item.amount)} SB`
  return ''
}
function tone(item) {
  if (!item) return ''
  if (item.type === 'car') return 'tone-car'
  if (item.type === 'sb') return 'tone-sb'
  return 'tone-cash'
}
function typeIcon(type) {
  return type === 'car' ? 'mdi-car-sports' : type === 'sb' ? 'mdi-lightning-bolt' : 'mdi-cash'
}
function storeName(item) {
  if (item == null) return ''
  if (typeof item === 'string' || typeof item === 'number') return String(item).trim()
  return String(item.store_name || item.storeName || item.carName || item.name || '').trim()
}
function carImage(item) {
  const name = storeName(item)
  return name ? `${CAR_CDN}/${name}.webp` : ''
}
function chance(value) {
  return `${value.toFixed(2)}%`
}
function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}
function shuffle(list) {
  const next = [...list]
  for (let index = next.length - 1; index > 0; index--) {
    const swap = Math.floor(Math.random() * (index + 1))
    ;[next[index], next[swap]] = [next[swap], next[index]]
  }
  return next
}
function take(list, count) {
  return list.length ? shuffle(list).slice(0, Math.min(count, list.length)) : []
}
function buildMix(cash, sb, cars) {
  const items = [...take(cash, 24), ...take(sb, 24), ...take(cars, 36)]
  return shuffle(items.length ? items : [...cash, ...sb])
}
function fallbackPool() {
  return {
    cash: cashAmounts.value.map((amount) => ({ type: 'cash', amount, label: `${formatAmount(amount)} $` })),
    sb: boostAmounts.value.map((amount) => ({ type: 'sb', amount, label: `${formatAmount(amount)} SB` })),
    cars: [],
  }
}
function pick(type = null) {
  if (type) {
    const group = pool.value[type === 'car' ? 'cars' : type] || []
    if (group.length) return group[Math.floor(Math.random() * group.length)]
    const typed = mix.value.filter((item) => item.type === type)
    if (typed.length) return typed[Math.floor(Math.random() * typed.length)]
    if (type === 'car') return { type: 'car', label: 'Random car', icon: 'car' }
  }
  const source = mix.value.length ? mix.value : buildMix(pool.value.cash, pool.value.sb, pool.value.cars)
  return source.length ? source[Math.floor(Math.random() * source.length)] : fallbackPool().cash[0]
}
function fillReel(winner) {
  const items = []
  const types = ['car', 'cash', 'sb']
  for (let index = 0; index < REEL_LEN; index++) {
    if (index === WIN_INDEX) {
      items.push({ ...winner, key: `win-${index}-${Math.random().toString(36).slice(2, 7)}`, winner: true })
      continue
    }
    const item = pick(types[Math.floor(Math.random() * 3)])
    items.push({ ...item, key: `fill-${index}-${item.type}-${Math.random().toString(36).slice(2, 7)}`, winner: false })
  }
  return items
}
function offsetFor(index) {
  const center = (STRIDE * 3 - GAP) / 2
  return -(index * STRIDE + PRIZE_H / 2 - center)
}
function resetVisuals() {
  state.value = 'idle'
  error.value = ''
  rewards.value = []
  claiming.value = false
  landed.value = [false, false, false]
  reels.value = [0, 1, 2].map(() => ({ items: [], offset: 0, transition: 'none', landed: false }))
}
function idleReels() {
  reels.value = [0, 1, 2].map((reel) => ({
    items: Array.from({ length: 12 }, (_, index) => {
      const item = pick(['car', 'cash', 'sb'][Math.floor(Math.random() * 3)])
      return { ...item, key: `idle-${reel}-${index}-${item.type}-${Math.random().toString(36).slice(2, 7)}`, winner: false }
    }),
    offset: offsetFor(1),
    transition: 'none',
    landed: false,
  }))
}
async function waitFade() {
  const el = reelsEl.value
  if (!el) return wait(FADE_MS)
  return new Promise((resolve) => {
    let done = false
    const finish = () => {
      if (done) return
      done = true
      el.removeEventListener('transitionend', onEnd)
      resolve()
    }
    const onEnd = (event) => {
      if (event.target === el && event.propertyName === 'opacity') finish()
    }
    el.addEventListener('transitionend', onEnd)
    window.setTimeout(finish, FADE_MS + 80)
  })
}
async function setSoft(value) {
  if (soft.value === value) return
  soft.value = value
  await nextTick()
  await waitFade()
}
async function loadPool() {
  if (poolReady.value && mix.value.length) return
  try {
    const result = await proxy.$api({ serviceName: 'premiumDrop', methodName: 'weeklyDropPool', parameters: [] })
    const cash = (Array.isArray(result?.cash) ? result.cash : []).filter((item) => Number(item?.amount || 0) <= cashMax.value)
    const sb = (Array.isArray(result?.sb) ? result.sb : []).filter((item) => Number(item?.amount || 0) <= sbMax.value)
    const cars = Array.isArray(result?.cars) ? result.cars : []
    if (!cash.length && !sb.length && !cars.length) {
      const fallback = fallbackPool()
      pool.value = fallback
      mix.value = buildMix(fallback.cash, fallback.sb, fallback.cars)
    } else {
      pool.value = { cash, sb, cars }
      mix.value = buildMix(cash, sb, cars)
    }
    poolReady.value = true
  } catch {
    const fallback = fallbackPool()
    pool.value = fallback
    mix.value = buildMix(fallback.cash, fallback.sb, fallback.cars)
    poolReady.value = true
  }
}
async function refreshDriver() {
  try {
    const result = await proxy.$api({ serviceName: 'session', methodName: 'GetUserInfo', parameters: [] })
    if (result?.persona) Driver.setPersona(result.persona)
    if (result?.user) Driver.setUser(result.user)
  } catch {
    // Claim response remains authoritative.
  }
}
async function spinReel(index, duration) {
  const reel = reels.value[index]
  reel.transition = 'none'
  reel.offset = offsetFor(0)
  await nextTick()
  await wait(30)
  reel.transition = `transform ${duration}ms cubic-bezier(0.05, 0.7, 0.08, 1)`
  reel.offset = offsetFor(WIN_INDEX)
  await wait(duration + 40)
  reel.landed = true
  landed.value[index] = true
}
async function claim(options = {}) {
  const fromRespin = !!options.fromRespin
  if (!fromRespin && (claiming.value || state.value === 'spinning' || state.value === 'done')) return
  if (fromRespin && (claiming.value || state.value === 'spinning')) return
  claiming.value = true
  error.value = ''
  state.value = 'spinning'
  landed.value = [false, false, false]
  try {
    if (fromRespin) {
      await setSoft(true)
      rewards.value = []
      reels.value = [0, 1, 2].map(() => ({ items: [], offset: 0, transition: 'none', landed: false }))
    }
    await loadPool()
    const result = await proxy.$api({
      serviceName: 'premiumDrop',
      methodName: normal.value ? 'wheelSpinClaim' : 'weeklyDropClaim',
      parameters: [],
    })
    if (!result?.ok || !Array.isArray(result.rewards) || result.rewards.length < 3) {
      if (fromRespin) await setSoft(false)
      state.value = 'error'
      error.value = result?.error || 'Claim failed.'
      claiming.value = false
      return
    }
    if (normal.value && result.wheelspins != null && Driver.user) {
      Driver.setUser({ ...Driver.user, wheelspins: result.wheelspins })
    }
    rewards.value = result.rewards.slice(0, 3)
    for (const reward of rewards.value) {
      if (reward.type === 'car') pool.value.cars = [reward, ...pool.value.cars]
      if (reward.type === 'cash') pool.value.cash = [reward, ...pool.value.cash]
      if (reward.type === 'sb') pool.value.sb = [reward, ...pool.value.sb]
    }
    mix.value = buildMix(pool.value.cash, pool.value.sb, pool.value.cars)
    reels.value = rewards.value.map((reward) => ({
      items: fillReel(reward),
      offset: offsetFor(0),
      transition: 'none',
      landed: false,
    }))
    await nextTick()
    if (fromRespin) {
      await setSoft(false)
      await wait(40)
    } else {
      await wait(60)
    }
    await Promise.all([spinReel(0, 7800), spinReel(1, 9800), spinReel(2, 11800)])
    await refreshDriver()
    state.value = 'done'
    emit('claimed', {
      mode: props.mode,
      rewards: rewards.value,
      wheelspins: normal.value ? result.wheelspins ?? 0 : undefined,
    })
  } catch (exception) {
    if (fromRespin) await setSoft(false)
    state.value = 'error'
    error.value = exception?.message || 'Claim failed.'
  } finally {
    claiming.value = false
  }
}
function close() {
  if (state.value !== 'spinning') open.value = false
}
function onAction() {
  if (state.value === 'done') {
    if (normal.value && props.wheelspins > 0) {
      claim({ fromRespin: true })
      return
    }
    close()
    return
  }
  claim()
}

watch(() => [props.modelValue, props.mode], async ([value]) => {
  if (!value) return
  poolReady.value = false
  mix.value = []
  resetVisuals()
  await loadPool()
  idleReels()
})
</script>

<template>
  <v-dialog
    v-model="open"
    width="820"
    scrim="rgba(0,0,0,0.7)"
    transition="dialog-bottom-transition"
    :persistent="state === 'spinning'"
  >
    <v-card class="drop-card" :class="{ 'mode-normal': normal }">
      <header class="drop-header">
        <div>
          <span class="drop-kicker">{{ kicker }}</span>
          <h2>{{ heading }}</h2>
          <p class="drop-subtitle">{{ subtitle || '\u00a0' }}</p>
        </div>
        <div class="drop-header-actions">
          <button
            type="button"
            class="drop-close"
            :disabled="state === 'spinning'"
            aria-label="Drop rates"
            title="Drop rates"
            @click="ratesOpen = true"
          >
            <v-icon size="18">mdi-chart-donut</v-icon>
          </button>
          <button
            type="button"
            class="drop-close"
            :disabled="state === 'spinning'"
            aria-label="Close"
            @click="close"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
      </header>
      <div class="drop-body">
        <div ref="reelsEl" class="reels" :class="{ soft }">
          <div
            v-for="(reel, index) in reels"
            :key="'reel-' + index"
            class="reel"
            :class="{ landed: reel.landed }"
          >
            <span class="marker marker-left" />
            <span class="marker marker-right" />
            <div class="reel-window">
              <div
                class="reel-strip"
                :style="{
                  transform: `translate3d(0, ${reel.offset}px, 0)`,
                  transition: reel.transition,
                  gap: `${GAP}px`,
                }"
              >
                <article
                  v-for="item in reel.items"
                  :key="item.key"
                  class="prize"
                  :class="[tone(item), { winner: item.winner && reel.landed, 'prize-car': item.type === 'car' && item.store_name }]"
                  :style="{ height: `${PRIZE_H}px` }"
                >
                  <template v-if="item.type === 'car' && item.store_name">
                    <img class="prize-car-img" :src="carImage(item)" :alt="item.label" loading="lazy" decoding="async">
                    <div class="prize-car-overlay">
                      <span v-if="brand(item)" class="prize-brand">{{ brand(item) }}</span>
                      <strong class="prize-model">{{ modelName(item) }}</strong>
                    </div>
                  </template>
                  <template v-else>
                    <div class="prize-media">
                      <v-icon size="26">{{ typeIcon(item.type) }}</v-icon>
                    </div>
                    <div class="prize-copy">
                      <template v-if="item.type === 'car'">
                        <span v-if="brand(item)" class="prize-brand">{{ brand(item) }}</span>
                        <strong class="prize-model">{{ modelName(item) }}</strong>
                      </template>
                      <strong v-else class="prize-amount">{{ moneyLabel(item) }}</strong>
                    </div>
                  </template>
                </article>
              </div>
            </div>
          </div>
        </div>
        <div class="drop-actions">
          <button type="button" class="spin-btn" :disabled="state === 'spinning'" @click="onAction">
            <v-icon size="18">{{ actionIcon }}</v-icon>
            {{ actionLabel }}
          </button>
        </div>
      </div>
    </v-card>
  </v-dialog>

  <v-dialog v-model="ratesOpen" width="520">
    <v-card class="rates-card" :class="{ 'mode-normal': normal }">
      <header class="rates-header">
        <div>
          <span class="drop-kicker">Transparency</span>
          <h2>Drop rates</h2>
        </div>
        <button type="button" class="drop-close" aria-label="Close" @click="ratesOpen = false">
          <v-icon>mdi-close</v-icon>
        </button>
      </header>
      <div class="rates-body">
        <p class="rates-lead">Each reel independently awards one of three reward types with equal probability.</p>
        <section class="rates-section">
          <h3>Reward type</h3>
          <ul class="rates-list">
            <li><span class="rates-label">Random car</span><strong>33.33%</strong></li>
            <li><span class="rates-label">Cash</span><strong>33.33%</strong></li>
            <li><span class="rates-label">SpeedBoost</span><strong>33.33%</strong></li>
          </ul>
        </section>
        <p class="rates-note">Cars are picked at random from droppable cars (equal weight).</p>
        <section class="rates-section">
          <h3>Cash amounts</h3>
          <p class="rates-note">Each amount has {{ chance(cashChance) }} probability per reel.</p>
          <ul class="rates-list rates-list-dense">
            <li v-for="amount in cashAmounts" :key="amount">
              <span class="rates-label">{{ amount.toLocaleString() }} $</span>
              <strong>{{ chance(cashChance) }}</strong>
            </li>
          </ul>
        </section>
        <section class="rates-section">
          <h3>Speedboost amounts</h3>
          <p class="rates-note">Each amount has {{ chance(boostChance) }} probability per reel.</p>
          <ul class="rates-list rates-list-dense">
            <li v-for="amount in boostAmounts" :key="amount">
              <span class="rates-label">{{ amount.toLocaleString() }} SB</span>
              <strong>{{ chance(boostChance) }}</strong>
            </li>
          </ul>
        </section>
      </div>
    </v-card>
  </v-dialog>
</template>
